import moment from 'moment'
import 'moment/locale/ka'
import { getCookie } from 'react-use-cookie';
import { CONFIG } from '../core';

export const getGtagClientIdAndSessionId = async () => {
  const clientIdPromise = new Promise((resolve, reject) => {
    if (!getCookie('_ga')) {
      return resolve(null);
    }

    try {
      window.gtag('get', process.env.REACT_APP_GTAG_ID, 'client_id', (clientId) => {
        resolve(clientId);
      });
    } catch (ex) {
      reject(null);
    }
  });

  const sessionIdPromise = new Promise((resolve, reject) => {
    if (!getCookie('_ga')) {
      return resolve(null);
    }

    try {
      window.gtag('get', process.env.REACT_APP_GTAG_ID, 'session_id', (sessionId) => {
        resolve(sessionId);
      });
    } catch (ex) {
      reject(null);
    }
  });

  const clientId = await clientIdPromise;
  const sessionId = await sessionIdPromise;

  return {
    clientId: clientId ? clientId : null,
    sessionId: sessionId ? sessionId : null,
  };
};

export const getClientId = () => {
  return new Promise((resolve, reject) => {
    if (!getCookie('_ga')) return resolve(null);

    window.gtag('get', process.env.REACT_APP_GTAG_ID, 'client_id', clientId => {
      if (clientId) {
        resolve(clientId);
      } else {
        reject('Failed to get Client ID');
      }
    });
  });
};

export const getSessionId = () => {
  return new Promise((resolve, reject) => {
    if (!getCookie('_ga')) return resolve(null);

    window.gtag('get', process.env.REACT_APP_GTAG_ID, 'session_id', (sessionId) => {
      if (sessionId) {
        resolve(sessionId);
      } else {
        reject('Failed to get Session ID');
      }
    });
  });
};

export const conditionTypes = {
  0: 'used',
  1: 'new',
  2: 'like_new',
  4: 'for_parts',
  5: 'outlet',
};

export const promotionTypes = {
  20: 'SVIP',
  15: 'VIP+',
  10: 'VIP',
};

export const bnplProviders = {
  16: 'TBC'
}

export const getProductSellType = (product) => {
  if (product.shop && product.shop_type_id) {
    return product.shop_type_id === 1 ? 'b2c_online' : 'b2c_offline';
  }

  if (product.shop) {
    return product.shop.type_id === 1 ? 'b2c_online' : 'b2c_offline';
  }

  return product.sell_online === 1 ? 'c2c_online' : 'c2c_offline';
};

export const makeProductItem = (product) => {
  const itemVariant = conditionTypes[product?.cond_type_id];
  const productSellType = getProductSellType(product);
  const [item_category, item_category2, item_category3, item_category4, item_category5] = product.category_tree ? product.category_tree : [];

  return {
    item_id: product.product_id,
    item_name: product?.lang_data?.title ?? '',
    affiliation: product.shop && product.shop.title ? product.shop.title : 'no-affiliation',
    discount: Number(product.discount),
    item_brand: product.brand_title ? product.brand_title : 'no-brand',
    item_category: item_category ? item_category?.title : 'no-category',
    item_category2: item_category2 ? item_category2?.title : 'no-category',
    item_category3: item_category3 ? item_category3?.title : 'no-category',
    item_category4: item_category4 ? item_category4?.title : 'no-category',
    item_category5: item_category5 ? item_category5?.title : 'no-category',
    item_variant: itemVariant ? itemVariant : 'no-variant',

    location_id: product?.location ?? '',
    price: Number(product?.price),

    promotion_name: promotionTypes[product?.vip] ?? '',
    promotion_color: Boolean(product?.color),
    currency: 'GEL',

    product_sell_type: productSellType,
    seller_user_id: String(product?.user_id ?? ''),
    shop_id: product?.shop?.description?.id_code ?? '',
  };
};

const cioVipTiers = {
  20: 'super_vip',
  15: 'vip_plus',
  10: 'vip',
};

const CioMinInstalmentPrice = 45;
const CioMaxInstalmentPrice = 14200;
const CioMinBnplPrice = 40;

export const makeCioProductItem = (product) => {
  const leafCategory = product?.category_tree?.length ? product.category_tree[product.category_tree.length - 1] : null;
  const priceValue = Number(product.price);
  const isShop = Boolean(product.shop_id);
  const isOnlineShopProduct = isShop && product.shop_type_id === 1;
  const isBnplEnabled = Boolean(product.bnpl_methods?.length) && priceValue >= CioMinBnplPrice;

  return {
    item_id: String(product.product_id),
    item_title: product.lang_data?.title ?? product.title ?? null,
    listing_image_url: product.photos?.[0]?.large ?? null,
    category_id: leafCategory ? leafCategory.title : null,
    category_path: product.category_tree?.length
      ? product.category_tree.map(category => category.title).join('/')
      : null,
    brand: product.brand_title ?? null,
    price_value: priceValue,
    price_original: product.discount > 0 ? Number(product.old_price) : null,
    is_negotiable: priceValue === 0,
    seller_id: String(product.user_id),
    seller_type: isShop ? 'shop' : 'private',
    seller_rating: product.rating ?? null,
    shop_id: product.shop_id ? String(product.shop_id) : null,
    condition: conditionTypes[product.cond_type_id] ?? null,
    location: product.location ?? null,
    is_vip: Number(product.vip) > 0,
    vip_tier: cioVipTiers[product.vip] ?? null,
    has_installment: priceValue >= CioMinInstalmentPrice && priceValue <= CioMaxInstalmentPrice,
    has_bnpl: isBnplEnabled,
    has_fast_loan: !isOnlineShopProduct,
    delivery_pickup: product.available_to_pick_up === 1,
    delivery_service: product.available_to_shipping === 1,
    delivery_express: Boolean(product.available_to_self_delivery),
    photo_count: product.photos_cnt ?? product.photos?.length ?? 0,
    has_video: Boolean(product.video_url),
    stock_count: isShop ? (product.quantity ?? null) : null,
    view_count: product.views,
    listing_age_days: product.order_date ? moment().diff(moment(product.order_date), 'days') : null,
    item_attributes: null,
  };
};

export const getVipStatisticsData = (props) => {
  const DEFAULT_PERCENT = CONFIG.DEFAULT_VIP_PROGRESSBAR_PERCENT

  const VIPStatisticsData = props.VIPStatisticsData || {};

  const chosenPromotions = (props.chosenPromotions?.length ? props.chosenPromotions : ['0']) ?? ['0'];
  const mainPromotionKey = chosenPromotions[chosenPromotions.length - 1];
  const isColorSelectedWithVIP = chosenPromotions.length > 1 && chosenPromotions.includes(String(CONFIG.PROM_ORDERS.COLOR));

  const colorMap = {
    [CONFIG.PROM_ORDERS.SUPER_VIP]: '#FF641F',
    [CONFIG.PROM_ORDERS.VIP_PLUS]: '#FEC900',
    [CONFIG.PROM_ORDERS.VIP]: '#4A6CFA',
  };
  const selectedColor = colorMap[mainPromotionKey];

  const formattedStatisticsData = Object.keys(VIPStatisticsData).reduce((prevValue, currentValue) => {
    const idMap = {
      color: [CONFIG.PROM_ORDERS.COLOR],
      none: 0,
      super_vip: [CONFIG.PROM_ORDERS.SUPER_VIP],
      vip: [CONFIG.PROM_ORDERS.VIP],
      vip_plus: [CONFIG.PROM_ORDERS.VIP_PLUS],
    };

    const newObj = { ...prevValue };

    // calculations for progress bar display percentage
    let viewsProgressBar = ((VIPStatisticsData[currentValue]?.views || 0) / 100) * DEFAULT_PERCENT + DEFAULT_PERCENT;
    let favoritesProgressBar = ((VIPStatisticsData[currentValue]?.favorites || 0) / 100) * DEFAULT_PERCENT + DEFAULT_PERCENT;
    let showFullNumberProgressBar = ((VIPStatisticsData[currentValue]?.show_full_number || 0) / 100) * DEFAULT_PERCENT + DEFAULT_PERCENT;

    // calculations for percentage number display
    let viewsDisplayPercent = VIPStatisticsData[currentValue]?.views;
    let favoritesDisplayPercent = VIPStatisticsData[currentValue]?.favorites;
    let showFullNumberDisplayPercent = VIPStatisticsData[currentValue]?.show_full_number;

    if (isColorSelectedWithVIP) {
      // calculations for progress bar display percentage
      viewsProgressBar += (VIPStatisticsData['color'].views / 100) * DEFAULT_PERCENT;
      favoritesProgressBar += (VIPStatisticsData['color'].favorites / 100) * DEFAULT_PERCENT;
      showFullNumberProgressBar += (VIPStatisticsData['color'].show_full_number / 100) * DEFAULT_PERCENT;

      // calculations for percentage number display
      viewsDisplayPercent += VIPStatisticsData['color'].views;
      favoritesDisplayPercent += VIPStatisticsData['color'].favorites;
      showFullNumberDisplayPercent += VIPStatisticsData['color'].show_full_number;
    }
    newObj[idMap[currentValue]] = {
      progressBar: [viewsProgressBar, favoritesProgressBar, showFullNumberProgressBar],
      displayPercent: [viewsDisplayPercent, favoritesDisplayPercent, showFullNumberDisplayPercent],
    };

    return newObj;
  }, {});

  return [
    {
      name: props.langs.Views,
      icon: 'remove-red-eye-icon-i',
      progressBarPercent: formattedStatisticsData[mainPromotionKey]?.progressBar[0],
      displayPercent: formattedStatisticsData[mainPromotionKey]?.displayPercent[0],
      color: selectedColor,
    },
    {
      name: props.langs.Calls,
      icon: 'phone-grey-icon-i',
      progressBarPercent: formattedStatisticsData[mainPromotionKey]?.progressBar[2],
      displayPercent: formattedStatisticsData[mainPromotionKey]?.displayPercent[2],
      color: selectedColor,
    },
    {
      name: props.langs.Favorites,
      icon: 'favorite-grey-icon-i',
      progressBarPercent: formattedStatisticsData[mainPromotionKey]?.progressBar[1],
      displayPercent: formattedStatisticsData[mainPromotionKey]?.displayPercent[1],
      color: selectedColor,
    },
  ];
};

export const formatDateIntoGeorgian = (date) => {
  if (!date) {
    return null
  }

  return moment(date).locale('ka').format('DD MMMM YYYY')
}

export const checkTimeLeft = (date) => {
  let dateNow = moment(new Date(), 'YYYY-MM-DD HH:mm:ss');
  let startDate = moment(date, 'YYYY-MM-DD HH:mm:ss');
  return startDate.diff(dateNow, 'seconds') > 0;
};